import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  Typography,
  IconButton,
  FormControlLabel,
  Switch,
} from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import InputText from "components/_refactor/text/input";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import DateField from "components/_refactor/fields/date";
import UploadFile from "components/_refactor/dropzone";
import ExistingLinkedFile from "./existing-linked-file";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import themeColorsDesingSystem from "assets/theme/ds-colors";
import {
  CONTENT_STATUS,
  SEGMENTATION_OPTIONS,
  ACCEPT_IMAGES,
} from "../constants";
import {
  createCampaign,
  editCampaign,
  listContents,
  uploadMarketingFiles,
  buildSegmentacao,
  buildTags,
} from "../services/franchise-marketing-api";
import {
  populateCampaignForm,
  getAgendadoExtraFields,
} from "../utils/form-helpers";

const EMPTY_FORM = {
  nome: "",
  descricao: "",
  categoria: null,
  dataInicio: null,
  dataFim: null,
  status: null,
  destaqueHome: false,
  ordemExibicao: "",
  segmentacao: null,
  tags: "",
  materiaisVinculados: [],
  videosVinculados: [],
  dataPublicacaoAgendada: "",
};

export default function DialogCampanha({
  open,
  onClose,
  onSaved,
  content,
  categoryOptions = [],
}) {
  const snackbar = useSnackbar();
  const [form, setForm] = useState(EMPTY_FORM);
  const [thumbnailFiles, setThumbnailFiles] = useState([]);
  const [bannerFiles, setBannerFiles] = useState([]);
  const [materialOptions, setMaterialOptions] = useState([]);
  const [videoOptions, setVideoOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileResetBase = `${open}-${content?.IdFranquiaMarketingCampanha || content?.Id || "new"}`;

  const loadLinkedOptions = async () => {
    try {
      const contents = await listContents();
      setMaterialOptions(
        contents
          .filter((c) =>
            ["MaterialPdf", "Institucional", "VmMerchandising"].includes(
              c.Tipo,
            ),
          )
          .map((c) => ({
            value: c.IdFranquiaMarketingConteudo,
            label: c.Titulo,
          })),
      );
      setVideoOptions(
        contents
          .filter((c) => ["Video", "Institucional"].includes(c.Tipo))
          .map((c) => ({
            value: c.IdFranquiaMarketingConteudo,
            label: c.Titulo,
          })),
      );
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!open) return;
    loadLinkedOptions();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    if (content) {
      const populated = populateCampaignForm(
        content,
        categoryOptions,
        materialOptions,
        videoOptions,
      );
      if (populated) {
        setForm(populated);
      }
    }
  }, [open, content, categoryOptions, materialOptions, videoOptions]);

  useEffect(() => {
    if (!open || content) return;
    setForm(EMPTY_FORM);
    setThumbnailFiles([]);
    setBannerFiles([]);
  }, [open, content]);

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.nome?.trim()) {
      snackbar.showMessage("Nome da campanha é obrigatório.", {
        severity: "error",
      });
      return;
    }

    if (!form.categoria?.value) {
      snackbar.showMessage("Categoria é obrigatória.", { severity: "error" });
      return;
    }

    if (!form.dataInicio || !form.dataFim) {
      snackbar.showMessage("Datas de início e fim são obrigatórias.", {
        severity: "error",
      });
      return;
    }

    setSaving(true);

    try {
      const campaignTitulo = form.nome.trim() || "campanha";
      const filePayload = await uploadMarketingFiles([
        {
          files: thumbnailFiles,
          fieldName: "thumbnail",
          titulo: campaignTitulo,
        },
        {
          files: bannerFiles,
          fieldName: "bannerPrincipal",
          titulo: `${campaignTitulo}_banner`,
        },
      ]);

      const conteudosVinculados = [
        ...form.materiaisVinculados.map((item) => ({
          idFranquiaMarketingConteudo: item.value,
          tipoVinculo: "Material",
        })),
        ...form.videosVinculados.map((item) => ({
          idFranquiaMarketingConteudo: item.value,
          tipoVinculo: "Video",
        })),
      ];

      const payload = {
        nome: form.nome.trim(),
        descricao: form.descricao,
        idFranquiaMarketingCategoria: form.categoria.value,
        dataInicio: form.dataInicio,
        dataFim: form.dataFim,
        status: form.status?.value || "Rascunho",
        destaqueHome: form.destaqueHome,
        ordemExibicao: Number(form.ordemExibicao) || 0,
        tags: buildTags(form.tags),
        segmentacao: buildSegmentacao(form.segmentacao),
        conteudosVinculados,
        ...getAgendadoExtraFields(form),
        ...filePayload,
      };

      const editId =
        content?.IdFranquiaMarketingCampanha || content?.id || content?.Id;

      if (editId) {
        await editCampaign({
          ...payload,
          idFranquiaMarketingCampanha: editId,
        });
        snackbar.showMessage("Campanha atualizada com sucesso.", {
          severity: "success",
        });
      } else {
        await createCampaign(payload);
        snackbar.showMessage("Campanha salva com sucesso.", {
          severity: "success",
        });
      }

      onSaved?.();
    } catch (error) {
      console.error(error);
      snackbar.showMessage(
        error?.message || "Falha ao salvar campanha.",
        { severity: "error" },
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Typography
          variant="h4"
          component="span"
          style={{ color: themeColorsDesingSystem.text.primary }}
        >
          {content ? "Editar Campanha" : "Cadastro de Campanha"}
        </Typography>
        <IconButton
          onClick={onClose}
          style={{ position: "absolute", right: 8, top: 8 }}
          aria-label="Fechar"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <InputText
                  label="Nome da campanha"
                  value={form.nome}
                  onChange={(e) => updateForm("nome", e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <InputText
                  label="Descrição"
                  value={form.descricao}
                  onChange={(e) => updateForm("descricao", e.target.value)}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Categoria"
                  value={form.categoria}
                  onChange={(v) => updateForm("categoria", v)}
                  options={categoryOptions}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              <Grid item xs={6}>
                <DateField
                  label="Data início"
                  value={form.dataInicio}
                  onChange={(v) => updateForm("dataInicio", v)}
                />
              </Grid>
              <Grid item xs={6}>
                <DateField
                  label="Data fim"
                  value={form.dataFim}
                  onChange={(v) => updateForm("dataFim", v)}
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Status"
                  value={form.status}
                  onChange={(v) => updateForm("status", v)}
                  options={CONTENT_STATUS}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.destaqueHome}
                      onChange={(e) =>
                        updateForm("destaqueHome", e.target.checked)
                      }
                      color="primary"
                    />
                  }
                  label="Destaque na home"
                  style={{ color: themeColorsDesingSystem.text.primary }}
                />
              </Grid>
              <Grid item xs={12}>
                <InputText
                  label="Ordem de exibição"
                  type="number"
                  value={form.ordemExibicao}
                  onChange={(e) => updateForm("ordemExibicao", e.target.value)}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  style={{ color: themeColorsDesingSystem.gray.dark }}
                >
                  Thumbnail
                </Typography>
                <UploadFile
                  message="Arraste a thumbnail ou clique para selecionar"
                  accept={ACCEPT_IMAGES}
                  onChangeFiles={setThumbnailFiles}
                  resetKey={`${fileResetBase}-thumbnail`}
                />
                <ExistingLinkedFile
                  url={content?.UrlThumbnail}
                  hidden={!!thumbnailFiles.length}
                  label="Thumbnail atual vinculada"
                  openLabel="Abrir thumbnail atual"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  style={{ color: themeColorsDesingSystem.gray.dark }}
                >
                  Banner principal
                </Typography>
                <UploadFile
                  message="Arraste o banner ou clique para selecionar"
                  accept={ACCEPT_IMAGES}
                  onChangeFiles={setBannerFiles}
                  resetKey={`${fileResetBase}-banner`}
                />
                <ExistingLinkedFile
                  url={content?.UrlBannerPrincipal}
                  hidden={!!bannerFiles.length}
                  label="Banner atual vinculado"
                  openLabel="Abrir banner atual"
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Segmentação"
                  value={form.segmentacao}
                  onChange={(v) => updateForm("segmentacao", v)}
                  options={SEGMENTATION_OPTIONS}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              {form.status?.value === "Agendado" && (
                <Grid item xs={12}>
                  <InputText
                    label="Data/hora de publicação agendada"
                    type="datetime-local"
                    value={form.dataPublicacaoAgendada}
                    onChange={(e) =>
                      updateForm("dataPublicacaoAgendada", e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <InputText
                  label="Tags"
                  value={form.tags}
                  onChange={(e) => updateForm("tags", e.target.value)}
                  placeholder="Ex: inverno, coleção, promoção"
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Materiais vinculados"
                  value={form.materiaisVinculados[0] || null}
                  onChange={(v) =>
                    updateForm("materiaisVinculados", v ? [v] : [])
                  }
                  options={materialOptions}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Vídeos vinculados"
                  value={form.videosVinculados[0] || null}
                  onChange={(v) => updateForm("videosVinculados", v ? [v] : [])}
                  options={videoOptions}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px" }}>
        <Button onClick={onClose} variant="outlined" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={saving}
        >
          Salvar campanha
        </Button>
      </DialogActions>
    </Dialog>
  );
}
